import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Card from '../common/Card';
import EmptyState from '../common/EmptyState';
import { Skeleton } from '../common/Skeleton';

const DashboardPage = ({ onNavigate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dashboardSummary, setDashboardSummary] = useState({
    latestDailyBusiness: null,
    latestStockCheck: null,
    latestLiterStatement: null,
    todayIncome: 0,
    todayExpenses: 0,
    lowStockCount: 0
  });
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [dateEntries, setDateEntries] = useState({}); // { 'YYYY-MM-DD': { dailyBusiness: [], stockCheck: [], literStatement: [] } }
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateModal, setShowDateModal] = useState(false);
  const [loadingEntries, setLoadingEntries] = useState(false);

  useEffect(() => {
    fetchDashboardSummary();
  }, []);

  useEffect(() => {
    fetchMonthEntries();
  }, [currentDate]);

  // Handle ESC key for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showDateModal) {
        setShowDateModal(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDateModal]);

  // Fetch entries for the current month
  const fetchMonthEntries = async () => {
    try {
      setLoadingEntries(true);
      if (!localStorage.getItem('token')) {
        setLoadingEntries(false);
        return;
      }

      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      // Fetch all entries in parallel with error handling
      const [dailyBusinessResponse, stockCheckResponse, literStatementResponse] = await Promise.allSettled([
        api.get('/daily-business', { params: { startDate, endDate } }),
        api.get('/stock/check-records', { params: { startDate, endDate } }),
        api.get('/liter-statements', { params: { month, year } })
      ]);

      // Organize entries by date
      const entriesByDate = {};

      // Helper function to parse date string as local date (avoiding timezone issues)
      const parseLocalDate = (dateString) => {
        if (!dateString) return null;
        // If it's already a date string in YYYY-MM-DD format, parse it as local
        const dateStr = dateString.split('T')[0] || dateString;
        if (dateStr.length === 10) {
          const [year, month, day] = dateStr.split('-').map(Number);
          // Create date in local timezone (month is 0-indexed in Date constructor)
          return new Date(year, month - 1, day);
        }
        return new Date(dateString);
      };

      // Helper function to format date as YYYY-MM-DD in local timezone
      const formatDateAsKey = (date) => {
        if (!date) return null;
        const d = date instanceof Date ? date : parseLocalDate(date);
        if (!d || isNaN(d.getTime())) return null;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Process Daily Business entries
      if (dailyBusinessResponse.status === 'fulfilled' && dailyBusinessResponse.value?.data?.records) {
        dailyBusinessResponse.value.data.records.forEach(record => {
          try {
            // Parse the date from database as local date to avoid timezone issues
            const dateObj = parseLocalDate(record.business_date);
            if (dateObj && !isNaN(dateObj.getTime())) {
              const date = formatDateAsKey(dateObj);
              if (date && date.length === 10 && date >= startDate && date <= endDate) {
                if (!entriesByDate[date]) entriesByDate[date] = { dailyBusiness: [], stockCheck: [], literStatement: [] };
                entriesByDate[date].dailyBusiness.push(record);
              }
            }
          } catch (error) {
            console.error('Error processing daily business entry:', error, record);
          }
        });
      }

      // Process Stock Check records
      if (stockCheckResponse.status === 'fulfilled' && stockCheckResponse.value?.data?.records) {
        stockCheckResponse.value.data.records.forEach(record => {
          try {
            // Parse the date from database as local date to avoid timezone issues
            const dateObj = parseLocalDate(record.check_date);
            if (dateObj && !isNaN(dateObj.getTime())) {
              const date = formatDateAsKey(dateObj);
              if (date && date.length === 10 && date >= startDate && date <= endDate) {
                if (!entriesByDate[date]) entriesByDate[date] = { dailyBusiness: [], stockCheck: [], literStatement: [] };
                entriesByDate[date].stockCheck.push(record);
              }
            }
          } catch (error) {
            console.error('Error processing stock check record:', error, record);
          }
        });
      }

      // Process Liter Statement entries (they are monthly, so we'll show them on the first day of the month)
      if (literStatementResponse.status === 'fulfilled' && literStatementResponse.value?.data?.statements) {
        literStatementResponse.value.data.statements.forEach(statement => {
          // Only show if it matches the current month
          if (statement.month === month && statement.year === year) {
            const date = `${statement.year}-${String(statement.month).padStart(2, '0')}-01`;
            if (!entriesByDate[date]) entriesByDate[date] = { dailyBusiness: [], stockCheck: [], literStatement: [] };
            entriesByDate[date].literStatement.push(statement);
          }
        });
      }

      setDateEntries(entriesByDate);
    } catch (error) {
      console.error('Error fetching month entries:', error);
      setDateEntries({});
    } finally {
      setLoadingEntries(false);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      setLoadingSummary(true);
      if (!localStorage.getItem('token')) return;

      const date = new Date();
      const today = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

      const [dbRes, scRes, lsRes, reportRes, stockRes] = await Promise.all([
        api.get('/daily-business', { params: { limit: 1 } }).catch(() => ({ data: { records: [] } })),
        api.get('/stock/check-records', { params: { limit: 1 } }).catch(() => ({ data: { records: [] } })),
        api.get('/liter-statements', { params: { limit: 1 } }).catch(() => ({ data: { statements: [] } })),
        api.get('/reports/summary', { params: { startDate: today, endDate: today } }).catch(() => ({ data: null })),
        api.get('/reports/stock').catch(() => ({ data: { stock: [] } }))
      ]);

      let todayIncome = 0;
      let todayExpenses = 0;
      if (reportRes.data && reportRes.data.kpis) {
        todayIncome = reportRes.data.kpis.totalRevenue || 0;
        todayExpenses = reportRes.data.kpis.totalExpenses || 0;
      }

      let lowStockCount = 0;
      if (stockRes.data && stockRes.data.stock) {
        lowStockCount = stockRes.data.stock.filter(item => item.isLowStock).length;
      }

      setDashboardSummary({
        latestDailyBusiness: (dbRes.data?.records || [])[0] || null,
        latestStockCheck: (scRes.data?.records || [])[0] || null,
        latestLiterStatement: (lsRes.data?.statements || [])[0] || null,
        todayIncome,
        todayExpenses,
        lowStockCount
      });
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
    } finally {
      setLoadingSummary(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const formatDateKey = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.error('Invalid date provided to formatDateKey');
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDateEntries = (date) => {
    const dateKey = formatDateKey(date);
    return dateEntries[dateKey] || { dailyBusiness: [], stockCheck: [], literStatement: [] };
  };

  const handleDateClick = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      console.error('Invalid date provided to handleDateClick');
      return;
    }
    
    const entries = getDateEntries(date);
    const hasEntries = entries.dailyBusiness.length > 0 || 
                      entries.stockCheck.length > 0 || 
                      entries.literStatement.length > 0;
    
    if (hasEntries) {
      setSelectedDate(date);
      setShowDateModal(true);
    }
  };

  const handleModuleIconClick = (module, date) => {
    if (!onNavigate) {
      console.error('onNavigate callback is not available');
      return;
    }

    const dateKey = formatDateKey(date);
    const entries = getDateEntries(date);
    
    let params = null;
    
    if (module === 'daily_business' && entries.dailyBusiness.length > 0) {
      params = { filterDate: dateKey, recordId: entries.dailyBusiness[0].id };
    } else if (module === 'stock_check' && entries.stockCheck.length > 0) {
      params = { filterDate: dateKey, recordId: entries.stockCheck[0].id };
    } else if (module === 'liter_statement' && entries.literStatement.length > 0) {
      // For liter statements, use the month/year from the statement
      const statement = entries.literStatement[0];
      params = { 
        filterMonth: `${statement.year}-${String(statement.month).padStart(2, '0')}`, 
        statementId: statement.id 
      };
    }

    setShowDateModal(false);
    
    try {
      if (module === 'daily_business') {
        onNavigate('daily-business', params);
      } else if (module === 'stock_check') {
        onNavigate('stock-check', params);
      } else if (module === 'liter_statement') {
        onNavigate('liter-statement', params);
      }
    } catch (error) {
      console.error('Error navigating:', error);
    }
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const today = new Date();

    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`}></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isToday = date.toDateString() === today.toDateString();
      const dateKey = formatDateKey(date);
      const entries = dateEntries[dateKey] || { dailyBusiness: [], stockCheck: [], literStatement: [] };
      const hasEntries = entries.dailyBusiness.length > 0 || 
                        entries.stockCheck.length > 0 || 
                        entries.literStatement.length > 0;
      
      days.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateClick(date)}
          aria-label={`${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}${hasEntries ? ', has entries' : ''}`}
          className={`p-2 sm:p-3 md:p-4 m-0.5 sm:m-1 rounded-lg sm:rounded-xl transition-all duration-200 relative text-left ${
            hasEntries ? 'cursor-pointer' : 'cursor-default'
          } ${
            isToday
              ? 'bg-gradient-to-br from-primary to-primary-dark text-white font-bold shadow-md scale-105 ring-2 ring-primary/40'
              : 'bg-white dark:bg-slate-800 hover:bg-gradient-to-br hover:from-primary/10 hover:to-accent/10 dark:text-slate-200 border border-slate-200/80 dark:border-slate-700 hover:border-primary/30 hover:shadow-md'
          }`}
        >
          <div className="text-sm sm:text-base md:text-lg font-semibold mb-0.5 sm:mb-1">{day}</div>
          {hasEntries && (
            <div className="flex gap-1 justify-center items-center mt-1">
              {entries.dailyBusiness.length > 0 && (
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center" title="Daily Business">
                  <i className="fas fa-clipboard-check text-white text-xs" aria-hidden="true"></i>
                </div>
              )}
              {entries.stockCheck.length > 0 && (
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center" title="Stock Check">
                  <i className="fas fa-boxes text-white text-xs" aria-hidden="true"></i>
                </div>
              )}
              {entries.literStatement.length > 0 && (
                <div className="w-5 h-5 rounded-full bg-yellow-500 flex items-center justify-center" title="Liter Statement">
                  <i className="fas fa-file-alt text-white text-xs" aria-hidden="true"></i>
                </div>
              )}
            </div>
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 px-2 sm:px-3">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-primary to-primary-dark rounded-xl p-4 sm:p-5 text-white shadow relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Welcome to Dashboard</h1>
        </div>
      </div>

      {/* Calendar Section */}
      <Card hover>
        <div className="flex justify-between items-center mb-4 sm:mb-6 flex-wrap gap-3 sm:gap-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold gradient-text">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <div className="flex gap-3">
            <button
              onClick={prevMonth}
              aria-label="Previous month"
              className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:from-primary hover:to-primary-dark hover:text-white hover:border-primary transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-lg btn-hover-lift"
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button
              onClick={nextMonth}
              aria-label="Next month"
              className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:from-primary hover:to-primary-dark hover:text-white hover:border-primary transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-lg btn-hover-lift"
            >
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2 sm:mb-3">
          {weekdays.map(day => (
            <div key={day} className="text-center font-bold py-2 sm:py-3 text-xs sm:text-sm uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-100 dark:border-slate-700">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {loadingEntries ? (
            Array.from({ length: 35 }).map((_, i) => (
              <Skeleton key={i} className="h-14 sm:h-20 md:h-24 rounded-lg sm:rounded-xl" />
            ))
          ) : (
            renderCalendar()
          )}
        </div>
      </Card>

      {/* Date Modal */}
      {showDateModal && selectedDate && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
          onClick={() => setShowDateModal(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowDateModal(false);
            }
          }}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="date-modal-title"
        >
          <div className="bg-white dark:bg-slate-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl max-h-dvh-90 overflow-y-auto border border-transparent dark:border-slate-700" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h3 id="date-modal-title" className="text-xl sm:text-2xl font-bold gradient-text">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
              <button
                onClick={() => setShowDateModal(false)}
                className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center transition-colors"
                aria-label="Close modal"
              >
                <i className="fas fa-times text-slate-600 dark:text-slate-300"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              {(() => {
                const entries = getDateEntries(selectedDate);
                const modules = [];
                
                if (entries.dailyBusiness.length > 0) {
                  modules.push({
                    name: 'Daily Business',
                    icon: 'fa-clipboard-check',
                    color: 'from-primary to-primary-dark',
                    count: entries.dailyBusiness.length,
                    module: 'daily_business'
                  });
                }
                
                if (entries.stockCheck.length > 0) {
                  modules.push({
                    name: 'Stock Check',
                    icon: 'fa-boxes',
                    color: 'from-green-500 to-emerald-600',
                    count: entries.stockCheck.length,
                    module: 'stock_check'
                  });
                }
                
                if (entries.literStatement.length > 0) {
                  modules.push({
                    name: 'Liter Statement',
                    icon: 'fa-file-alt',
                    color: 'from-yellow-500 to-orange-500',
                    count: entries.literStatement.length,
                    module: 'liter_statement'
                  });
                }
                
                if (modules.length === 0) {
                  return (
                    <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                      <i className="fas fa-inbox text-4xl mb-2"></i>
                      <p>No entries for this date</p>
                    </div>
                  );
                }
                
                return modules.map((module, index) => {
                  let gradientClass = '';
                  if (module.module === 'daily_business') {
                    gradientClass = 'bg-gradient-to-r from-primary to-primary-dark';
                  } else if (module.module === 'stock_check') {
                    gradientClass = 'bg-gradient-to-r from-green-500 to-emerald-600';
                  } else if (module.module === 'liter_statement') {
                    gradientClass = 'bg-gradient-to-r from-yellow-500 to-orange-500';
                  }
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleModuleIconClick(module.module, selectedDate)}
                      className={`w-full p-4 rounded-2xl ${gradientClass} hover:shadow-lg transition-all duration-300 flex items-center justify-between group`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shadow-lg">
                          <i className={`fas ${module.icon} text-white text-lg`}></i>
                        </div>
                        <div className="text-left">
                          <div className="font-bold text-lg text-white">{module.name}</div>
                          <div className="text-sm text-white/80">{module.count} {module.count === 1 ? 'entry' : 'entries'}</div>
                        </div>
                      </div>
                      <i className="fas fa-chevron-right text-white/80 group-hover:translate-x-1 transition-transform"></i>
                    </button>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Summary */}
      {loadingSummary ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-6">
          {/* Today's Income & Expenses */}
          <Card className="flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                <i className="fas fa-wallet text-lg"></i>
              </div>
              <h3 className="text-lg font-bold">Today's Daily Business</h3>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                <div className="text-xs text-slate-500 mb-1">Income</div>
                <div className="font-bold text-green-600">₹{(dashboardSummary.todayIncome || 0).toLocaleString()}</div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 border border-slate-100 dark:border-slate-700">
                <div className="text-xs text-slate-500 mb-1">Expenses</div>
                <div className="font-bold text-red-600">₹{(dashboardSummary.todayExpenses || 0).toLocaleString()}</div>
              </div>
            </div>
            {dashboardSummary.latestDailyBusiness && (
              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                <div className="text-xs text-slate-500">Latest Entry</div>
                <div className="text-sm font-semibold">{new Date(dashboardSummary.latestDailyBusiness.business_date).toLocaleDateString()}</div>
              </div>
            )}
          </Card>

          {/* Stock Info */}
          <Card className="flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 flex items-center justify-center">
                <i className="fas fa-boxes text-lg"></i>
              </div>
              <h3 className="text-lg font-bold">Stock Status</h3>
            </div>
            <div className="flex-1 flex flex-col justify-center">
              <div className={`p-4 rounded-xl border ${dashboardSummary.lowStockCount > 0 ? 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-800' : 'bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-800'}`}>
                <div className="flex items-center gap-3">
                  <i className={`fas ${dashboardSummary.lowStockCount > 0 ? 'fa-exclamation-triangle text-red-500' : 'fa-check-circle text-green-500'} text-2xl`}></i>
                  <div>
                    <div className={`font-bold ${dashboardSummary.lowStockCount > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                      {dashboardSummary.lowStockCount > 0 ? `${dashboardSummary.lowStockCount} Low Stock Alerts` : 'Stock Levels Good'}
                    </div>
                    <div className="text-xs opacity-80 mt-0.5">Across all brands</div>
                  </div>
                </div>
              </div>
            </div>
            {dashboardSummary.latestStockCheck && (
              <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                <div className="text-xs text-slate-500">Latest Stock Check</div>
                <div className="text-sm font-semibold">{new Date(dashboardSummary.latestStockCheck.check_date).toLocaleDateString()}</div>
              </div>
            )}
          </Card>

          {/* Liter Statement */}
          <Card className="flex flex-col">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400 flex items-center justify-center">
                <i className="fas fa-file-alt text-lg"></i>
              </div>
              <h3 className="text-lg font-bold">Liter Statement</h3>
            </div>
            <div className="flex-1 flex flex-col justify-center">
              {dashboardSummary.latestLiterStatement ? (
                <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-100 dark:border-slate-700">
                  <div className="text-xs text-slate-500 mb-1">Latest Submission</div>
                  <div className="font-bold text-lg mb-1">
                    {dashboardSummary.latestLiterStatement.month}/{dashboardSummary.latestLiterStatement.year}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    <i className="fas fa-clock mr-1.5"></i>
                    {new Date(dashboardSummary.latestLiterStatement.created_at || new Date()).toLocaleDateString()}
                  </div>
                </div>
              ) : (
                <div className="text-center text-slate-500 py-4">
                  No liter statements found
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
